/* ----------------------------------------------------------------------------
 * RecommendPanel — conversational-actions (owner GO 12-10-2026 20:30 UTC): Aura's
 * numbered, priority-ordered list ("what do you recommend?" / "my pending blogs").
 * Numbers match what Aura says ("tell me the number or name"); the list scrolls when
 * it is taller than the popup. A tap on a row = picking that number; "Do all" = "all".
 * Presentational only: picks are handed to the P1 publish read-back / the write flow.
 * -------------------------------------------------------------------------- */

import { cn } from '@/lib/utils';
import type { RecommendView } from './useRecommendVoice.ts';

export interface RecommendPanelProps {
  view: RecommendView;
  onPick: (n: number) => void;
  onPickAll: () => void;
  onChooseSite: (siteId: number) => void;
  onDismiss: () => void;
  variant?: 'voice' | 'rail';
}

export function RecommendPanel({ view, onPick, onPickAll, onChooseSite, onDismiss, variant = 'voice' }: RecommendPanelProps) {
  const dark = variant === 'voice';
  const t = {
    box: dark ? 'border-white/10 bg-white/[0.03] text-[#e8e9ee]' : 'border-border-default bg-canvas/40 text-text-primary',
    sub: dark ? 'text-[#9aa0ad]' : 'text-text-tertiary',
    row: dark ? 'border-white/10 bg-[#0f0e16] hover:bg-white/5' : 'border-border-default bg-surface hover:bg-canvas',
    btn: dark ? 'border-white/15 bg-white/5 text-[#e8e9ee] hover:bg-white/10' : 'border-border-default bg-surface text-text-primary hover:bg-canvas',
    primary: dark ? 'border-[#a78bfa]/50 bg-[#a78bfa]/20 text-white hover:bg-[#a78bfa]/30' : 'border-ai/60 bg-ai-wash/60 text-text-primary hover:border-ai hover:bg-ai-wash',
  };
  const asking = view.phase === 'asking';
  const pub = view.items.some((i) => i.kind === 'publish');
  const wr = view.items.some((i) => i.kind === 'write');
  const allLabel = pub && wr ? 'Publish and write all' : pub ? 'Publish all' : 'Write all';
  return (
    <section className={cn('flex flex-col gap-2 rounded-xl border p-3 text-[12px]', t.box)} aria-label="Aura’s recommendations" aria-busy={view.phase === 'loading'}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium leading-snug">{view.line || 'Looking at what’s ready…'}</p>
        <button type="button" onClick={onDismiss} className={cn('rounded-md border px-2 py-0.5 text-[11px]', t.btn)} aria-label="Close">Close</button>
      </div>
      {view.phase === 'choose_site' && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Choose a site">
          {view.sites.map((s) => (
            <button key={s.id} type="button" onClick={() => onChooseSite(s.id)} className={cn('min-h-[40px] rounded-md border px-3 py-1.5', t.btn)}>{s.name}</button>
          ))}
        </div>
      )}
      {view.items.length > 0 && view.phase !== 'handed_off' && (
        <ol className="flex max-h-[45vh] flex-col gap-1.5 overflow-y-auto" aria-label="Recommended blogs, numbered">
          {view.items.map((i) => (
            <li key={i.n}>
              <button type="button" disabled={!asking} onClick={() => onPick(i.n)} className={cn('flex w-full min-h-[40px] items-start gap-2 rounded-lg border p-2 text-left disabled:opacity-60', t.row)}>
                <span className="w-5 shrink-0 font-semibold tabular-nums">{i.n}.</span>
                <span className="flex flex-col">
                  <span className="leading-snug">{i.title}</span>
                  <span className={cn('text-[11px]', t.sub)}>
                    {i.kind === 'publish' ? (i.state === 'on_site_as_draft' ? 'Ready — already on the site as a draft' : 'Ready to publish') : 'To write' + (i.keyword && i.keyword !== i.title ? ' · ' + i.keyword : '')}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
      {asking && view.items.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onPickAll} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold', t.primary)}>{allLabel}</button>
          <button type="button" onClick={onDismiss} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold', t.btn)}>Not now</button>
        </div>
      )}
      {view.writeNote && <p className={cn('text-[11px]', t.sub)} role="status">{view.writeNote}</p>}
      {view.error && <p className="text-[11px] text-red-400" role="alert">{view.error}</p>}
    </section>
  );
}
